import {useEffect, useRef, useState} from 'react';

// Adapted from https://hooks-guide.netlify.app/community/useThrottle

/**
 * Throttle updates to `value` to once-per-`limit` ms. Intermediate values may never be exposed if a new value
 * is passed in before the throttle period has expired.
 *
 * @note Make sure to memoize `value`s for which shallow equality is not sufficient, or `useThrottle` will
 * trigger unnecessary rerenders in the calling component.
 *
 * @note To wait until the end of the period in which the input is rapidly changing to update the output
 * use `useDebounce` instead.
 *
 * @example
 * const [input, setInput] = useState('');
 * const throttledInput = useThrottle(input, 1000);
 * useEffect(() => {
 *   // Search function will get called at most once per second.
 *   search(throttledInput)
 * }, [throttledInput]);
 */
export default function useThrottle<T>(value: T, limit: number): T {
  const [throttledValue, setThrottledValue] = useState(value);
  const lastRan = useRef(Date.now());

  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        if (Date.now() - lastRan.current >= limit) {
          setThrottledValue(value);
          lastRan.current = Date.now();
        }
      },
      limit - (Date.now() - lastRan.current)
    );

    return () => {
      clearTimeout(timeoutId);
    };
  }, [value, limit]);

  return throttledValue;
}
